/**
 * The AI follow-up's view of a MaxDiff answer, in both modes: the picks under the RESEARCHER'S own
 * anchor words (ADR-0003 §2), plus the options shown and left alone. Hardcoded anchors made a
 * probe on "Most likely to buy" a general preference question; with only the picks, the generator
 * cannot ask why an option was passed over, never having learned it was on screen.
 */
import { Anchors, BestWorstAnswer, formatBestWorstAnswer } from './bestWorst';

const CONTEXT_BLOCK_SEPARATOR = '\n\n';
const CONTEXT_LINE_SEPARATOR = '\n';
const OPTION_SEPARATOR = ', ';

/** On the respondent's screen, and on neither side of their answer. */
export const UNCHOSEN_LABEL = 'Shown but not chosen';

export interface MultiSetMaxDiffResponse {
  most?: string;
  least?: string;
  most_appealing?: string;
  least_appealing?: string;
}

export interface MaxDiffRankedClaims {
  topClaims: string[];
  bottomClaims: string[];
}

/**
 * What was shown and not picked, in the order it was seen. De-duplicated: multi-set MaxDiff shows
 * a claim in several sets, and listing it twice reads as two options that were both passed over.
 */
export function unchosenOptions(shown: readonly string[], chosen: Iterable<string>): string[] {
  const picked = new Set(chosen);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const option of shown) {
    if (picked.has(option) || seen.has(option)) continue;
    seen.add(option);
    out.push(option);
  }
  return out;
}

/** Every pick, either way, across every set — both spellings, as the claim scorer reads them. */
export function chosenMaxDiffOptions(
  responses: Record<number, MultiSetMaxDiffResponse | undefined>,
): string[] {
  const picks: string[] = [];
  for (const response of Object.values(responses)) {
    const most = response?.most_appealing || response?.most;
    const least = response?.least_appealing || response?.least;
    if (most) picks.push(most);
    if (least) picks.push(least);
  }
  return picks;
}

const unchosenBlock = (unchosen: readonly string[]): string | null =>
  unchosen.length === 0 ? null : `${UNCHOSEN_LABEL}: ${unchosen.join(OPTION_SEPARATOR)}`;

const joinBlocks = (blocks: (string | null)[]): string | undefined => {
  const kept = blocks.filter((block): block is string => !!block);
  return kept.length > 0 ? kept.join(CONTEXT_BLOCK_SEPARATOR) : undefined;
};

/**
 * Rendered by `formatBestWorstAnswer`, the one line the researcher's view and the backend's session
 * context also print. Nothing picked yields NO context, as before — "every option was passed over"
 * is a finding the respondent never made.
 */
export function buildBestWorstFollowupContext(
  answer: BestWorstAnswer,
  anchors: Anchors,
  shown: readonly string[],
): string | undefined {
  const picks = formatBestWorstAnswer(answer, anchors);
  if (!picks) return undefined;
  const chosen = [...answer.best, ...answer.worst].map((entry) => entry.option);
  return joinBlocks([picks, unchosenBlock(unchosenOptions(shown, chosen))]);
}

const claimsBlock = (anchor: string, claims: readonly string[]): string | null =>
  claims.length === 0
    ? null
    : [
        `Top ${claims.length} for "${anchor}":`,
        ...claims.map((claim, index) => `${index + 1}. ${claim}`),
      ].join(CONTEXT_LINE_SEPARATOR);

/** The multi-set equivalent. Nothing scored means nothing picked, so there is nothing to probe. */
export function buildMaxDiffFollowupContext(
  claims: MaxDiffRankedClaims,
  anchors: Anchors,
  shown: readonly string[],
  chosen: Iterable<string>,
): string | undefined {
  if (claims.topClaims.length === 0 && claims.bottomClaims.length === 0) return undefined;
  return joinBlocks([
    claimsBlock(anchors.best, claims.topClaims),
    claimsBlock(anchors.worst, claims.bottomClaims),
    unchosenBlock(unchosenOptions(shown, chosen)),
  ]);
}
